import React from "react";
import { css } from "@emotion/react";
import { useTranslation } from "react-i18next";
import { MainBlock } from "main/javascripts/components/block/MainBlock";
import { BasicBlock } from "main/javascripts/components/block/BasicBlock";
import { FlightPriceList } from "main/javascripts/features/lp/flightDstRegion/components/FlightPriceList";
import { nl2br } from "main/javascripts/utils/StringUtil";
import { BlockTitle } from "main/javascripts/components/molecules/Lp/BlockTitle";
import { BlockSubTitle } from "main/javascripts/components/molecules/Lp/BlockSubTitle";
import { Paragraph } from "main/javascripts/components/atoms/Paragraph";
import { Region } from "main/javascripts/models/Region";
import { Airport } from "main/javascripts/models/Airport";
import { RegionList } from "main/javascripts/components/molecules/Lp/RegionList";
import { Breadcrumb } from "main/javascripts/components/molecules/Breadcrumb";
import { Country } from "main/javascripts/models/Country";
import { useAppSelector } from "main/javascripts/store";
import { flightDestinationRegionSelector } from "../lpFlightDstRegionSlice";
import { space } from "main/javascripts/styles/base/spaceStyle";
import {
  backgroundColor,
  borderColor,
} from "main/javascripts/styles/base/colorStyle";
import { maxWidth } from "main/javascripts/styles/base/maxWidthStyle";
import { ChatEntry } from "main/javascripts/components/chat/ChatEntry";

export const LpFlightDstRegionIndexMain = (): React.ReactElement => {
  const { t } = useTranslation(["component"]);

  const flightDestinationRegion = useAppSelector(
    flightDestinationRegionSelector
  );

  const breadcrumbData: any[] = [{ url: "/", label: t("label:common.home") }];
  if (flightDestinationRegion) {
    if (
      flightDestinationRegion.country &&
      flightDestinationRegion.region.regionType !== "country"
    ) {
      const country: Country = flightDestinationRegion.country;
      breadcrumbData.push({
        url: `/lp/flights/dst_regions/${country.code}`,
        label: country.name,
      });
    }
    breadcrumbData.push({
      label: flightDestinationRegion.region.name,
    });
  }

  const renderBreadcrumb = (): JSX.Element => {
    return (
      <div css={breadcrumbBlockStyle}>
        <div css={breadcrumbInnerStyle}>
          <Breadcrumb linkData={breadcrumbData} />
        </div>
      </div>
    );
  };

  const renderAccessToCity = (): JSX.Element | null => {
    if (!flightDestinationRegion) {
      return null;
    }
    const region: Region | undefined = flightDestinationRegion.region;
    if (
      region &&
      region.airports &&
      region.airports.findIndex(
        (airport: Airport) => !!airport.accessToCity
      ) !== -1
    ) {
      return (
        <BasicBlock styleKey="blockWide">
          <section>
            <BlockTitle>{t("lp.accessToCity")}</BlockTitle>
            {region.airports.map((airport: Airport, index: number) => {
              return airport.accessToCity ? (
                <section key={index}>
                  <BlockSubTitle>{airport.shortName}</BlockSubTitle>
                  <Paragraph>{nl2br(airport.accessToCity)}</Paragraph>
                </section>
              ) : (
                ""
              );
            })}
          </section>
        </BasicBlock>
      );
    }
    return null;
  };

  const renderPriceList = (): JSX.Element | null => {
    if (!flightDestinationRegion) {
      return null;
    }
    const regionName: string = flightDestinationRegion.region.name;
    const flightCheapestRoundTrips =
      flightDestinationRegion.flightCheapestRoundTrips;
    if (flightCheapestRoundTrips && flightCheapestRoundTrips.length > 0) {
      return (
        <div css={priceListBlockStyle}>
          <section>
            <FlightPriceList
              originName={regionName}
              flightCheapestRoundTrips={flightCheapestRoundTrips}
            />
          </section>
        </div>
      );
    }
    return null;
  };

  const renderDescription = (): JSX.Element | null => {
    if (!flightDestinationRegion) {
      return null;
    }
    const regionName: string = flightDestinationRegion.region.name;
    if (flightDestinationRegion.region.description) {
      return (
        <>
          <BlockTitle>
            {t("lp.destinationAbout", { destination: regionName })}
          </BlockTitle>
          <Paragraph>
            {nl2br(flightDestinationRegion.region.description)}
          </Paragraph>
        </>
      );
    }
    return null;
  };

  const renderBestSeason = (): JSX.Element | null => {
    if (!flightDestinationRegion) {
      return null;
    }
    const regionName: string = flightDestinationRegion.region.name;
    if (flightDestinationRegion.region.bestSeason) {
      return (
        <section>
          <BlockSubTitle>
            {t("lp.destinationBestSeason", { destination: regionName })}
          </BlockSubTitle>
          <Paragraph>
            {nl2br(flightDestinationRegion.region.bestSeason)}
          </Paragraph>
        </section>
      );
    }
    return null;
  };

  const renderComment = (): JSX.Element | null => {
    if (!flightDestinationRegion) {
      return null;
    }
    if (flightDestinationRegion.region.comment) {
      return (
        <section>
          <BlockSubTitle>{t("lp.conciergeComment")}</BlockSubTitle>
          <Paragraph>{nl2br(flightDestinationRegion.region.comment)}</Paragraph>
        </section>
      );
    }
    return null;
  };

  const renderMainAirline = (): JSX.Element | null => {
    if (!flightDestinationRegion) {
      return null;
    }
    const regionName: string = flightDestinationRegion.region.name;
    if (flightDestinationRegion.region.mainAirline) {
      return (
        <BasicBlock styleKey="blockWide">
          <section>
            <BlockTitle>
              {t("lp.destinationMainAirlines", { destination: regionName })}
            </BlockTitle>
            <Paragraph>
              {nl2br(flightDestinationRegion.region.mainAirline)}
            </Paragraph>
          </section>
        </BasicBlock>
      );
    }
    return null;
  };

  const renderSurroundingRegions = (): JSX.Element | null => {
    if (!flightDestinationRegion) {
      return null;
    }
    if (flightDestinationRegion.surroundingRegions) {
      return (
        <BasicBlock styleKey="blockWide">
          <section>
            <BlockTitle>{t("lp.surroundingRegions")}</BlockTitle>
            <RegionList
              regions={flightDestinationRegion.surroundingRegions}
              linkUrl="/lp/flights/dst_regions/"
            />
          </section>
        </BasicBlock>
      );
    }
    return null;
  };

  if (!flightDestinationRegion) {
    return null;
  }
  return (
    <MainBlock>
      <div css={blockStyle}>
        {renderBreadcrumb()}
        <article>
          {renderPriceList()}
          <div css={infoBlockStyle}>
            <section>
              {renderDescription()}
              {renderBestSeason()}
              {renderComment()}
            </section>
          </div>
          <BasicBlock styleKey="blockWide">
            <section css={chatEntryBlockStyle}>
              <ChatEntry />
            </section>
          </BasicBlock>
          {renderAccessToCity()}
          {renderMainAirline()}
          {renderSurroundingRegions()}
        </article>
      </div>
    </MainBlock>
  );
};

const blockStyle = css`
  padding: 0 0 3rem;
`;
const infoBlockStyle = css`
  padding: ${space.atom3x} ${space.atom} ${space.atom2x};
  max-width: ${maxWidth.page1colWide};
  margin: 0 auto;
  box-sizing: border-box;
`;
const chatEntryBlockStyle = css`
  margin-left: ${space.atom};
  margin-right: ${space.atom};
`;
const priceListBlockStyle = css`
  padding: 0 0 ${space.atom2x};
  max-width: 100%;
  box-sizing: border-box;
  background-color: ${backgroundColor.secondaryLightColor};
`;
const breadcrumbBlockStyle = css`
  padding: ${space.atom} ${space.atom2x};
  max-width: ${maxWidth.page1colWide};
  margin: 0 auto;
  box-sizing: border-box;
`;
const breadcrumbInnerStyle = css`
  padding: ${space.atom0_5x} ${space.atom};
  box-sizing: border-box;
  border-top: 1px solid ${borderColor.secondaryLightColor};
  border-left: 0;
  border-right: 0;
`;
